import {PRISMAX_HEX_URL} from '../configs/config';

export const HEX_REFINEMENTS = Object.freeze({
    closestMatch: 'closest_match',
    moreVariation: 'more_variation',
    quality90Plus: 'quality_90_plus',
    differentMachines: 'different_machines',
    includeFailedAttempts: 'include_failed_attempts',
});

function authHeaders(token) {
    const headers = {'Content-Type': 'application/json'};
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
}

async function readError(response) {
    let detail;
    try {
        const data = await response.json();
        detail = data && (data.detail || data.message || data.error);
    } catch (_e) {
        // body wasn't JSON — fall through to status text
    }
    return detail || `Hex request failed (${response.status})`;
}

async function postJson(path, {token, body, signal} = {}) {
    const response = await fetch(`${PRISMAX_HEX_URL}${path}`, {
        method: 'POST',
        headers: authHeaders(token),
        body: body ? JSON.stringify(body) : undefined,
        signal,
    });
    if (!response.ok) {
        throw new Error(await readError(response));
    }
    return response.json();
}

export async function createHexSession({seedEpisodeIds, token, signal}) {
    const seeds = Array.isArray(seedEpisodeIds)
        ? seedEpisodeIds.map((value) => Number(value)).filter((value) => Number.isFinite(value))
        : [];
    return postJson('/v1/sessions', {
        token,
        body: {seed_episode_ids: seeds},
        signal,
    });
}

/**
 * Reader for Server-Sent Events streamed by /v1/sessions/{id}/chat and /refine.
 * Each event has shape `{ type, data }`. `onEvent` is called for every parsed event.
 * Resolves when the server closes the stream or sends `done`.
 */
async function streamSse(path, {token, body, signal, onEvent}) {
    const response = await fetch(`${PRISMAX_HEX_URL}${path}`, {
        method: 'POST',
        headers: {
            ...authHeaders(token),
            Accept: 'text/event-stream',
        },
        body: body ? JSON.stringify(body) : undefined,
        signal,
    });
    if (!response.ok) {
        throw new Error(await readError(response));
    }
    if (!response.body) {
        throw new Error('Hex stream missing response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    function flushEvent(rawEvent) {
        if (!rawEvent) return;
        let eventType = 'message';
        const dataLines = [];
        for (const line of rawEvent.split('\n')) {
            if (!line) continue;
            if (line.startsWith(':')) continue;
            if (line.startsWith('event:')) {
                eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
            }
        }
        if (!dataLines.length) return;
        const dataText = dataLines.join('\n');
        let parsed = null;
        try {
            parsed = JSON.parse(dataText);
        } catch (_e) {
            parsed = {raw: dataText};
        }
        try {
            onEvent({type: eventType, data: parsed});
        } catch (handlerErr) {
            // Don't kill the stream over a handler bug.
            // eslint-disable-next-line no-console
            console.error('Hex SSE handler error', handlerErr);
        }
    }

    try {
        while (true) {
            const {value, done} = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, {stream: true});
            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
                const rawEvent = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);
                flushEvent(rawEvent);
                boundary = buffer.indexOf('\n\n');
            }
        }
        if (buffer.trim()) {
            flushEvent(buffer);
        }
    } finally {
        try {
            reader.releaseLock();
        } catch (_e) {
            // ignore
        }
    }
}

export function streamHexChat({sessionId, message, refinement, seedEpisodeIds, token, signal, onEvent}) {
    if (!sessionId) {
        return Promise.reject(new Error('Hex session not initialized'));
    }
    const body = {message};
    if (refinement) {
        body.refinement = refinement;
    }
    if (Array.isArray(seedEpisodeIds)) {
        body.seed_episode_ids = seedEpisodeIds
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value));
    }
    return streamSse(`/v1/sessions/${encodeURIComponent(sessionId)}/chat`, {
        token,
        body,
        signal,
        onEvent,
    });
}

export function streamHexRefinement({sessionId, refinement, seedEpisodeIds, token, signal, onEvent}) {
    if (!sessionId) {
        return Promise.reject(new Error('Hex session not initialized'));
    }
    if (!refinement) {
        return Promise.reject(new Error('Hex refinement is required'));
    }
    const body = {refinement};
    if (Array.isArray(seedEpisodeIds)) {
        body.seed_episode_ids = seedEpisodeIds
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value));
    }
    return streamSse(`/v1/sessions/${encodeURIComponent(sessionId)}/refine`, {
        token,
        body,
        signal,
        onEvent,
    });
}

export async function checkHexHealth({signal} = {}) {
    const response = await fetch(`${PRISMAX_HEX_URL}/health`, {signal});
    if (!response.ok) {
        throw new Error(`Hex health failed (${response.status})`);
    }
    return response.json();
}

export async function submitHexCapabilityRequest({
    sessionId,
    pendingToken,
    approve,
    token,
    signal,
}) {
    if (!sessionId) {
        throw new Error('Hex session not initialized');
    }
    if (!pendingToken) {
        throw new Error('Missing capability request token');
    }
    return postJson(`/v1/sessions/${encodeURIComponent(sessionId)}/capability-request`, {
        token,
        body: {
            pending_token: pendingToken,
            approve: !!approve,
        },
        signal,
    });
}

export async function fetchEpisodeMetadata({episodeIds, token, signal}) {
    const ids = Array.isArray(episodeIds)
        ? episodeIds.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
        : [];
    if (!ids.length) {
        throw new Error('At least one valid episode ID is required');
    }
    return postJson('/v1/episode-metadata', {
        token,
        body: {episode_ids: ids},
        signal,
    });
}
